import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  Alert,
  Select,
  MenuItem,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  CircularProgress,
  Backdrop,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'
import { darken, lighten } from '@mui/material/styles'
import {
  Check as CheckIcon,
  AutoAwesome as AutoAwesomeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { UUID } from 'crypto'
import {
  FundingNeedCreate,
  FundingNeedDraft,
  FundingNeedResponse,
  FundingNeedUpdate,
  FundingNeedDataAssessmentResponse,
} from '@/models/FundingNeeds'
import { useFundingNeeds, useFundingNeedDocs } from '@/utils/hooks/fundingNeeds'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import LanguageIcon from '@mui/icons-material/Language'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import UploadFundingNeedFile from '../UploadFundingNeedFile'
import UploadFundingNeedWebsiteUrls from '../UploadFundingNeedWebsiteUrl'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { useRegions, useRegionSets } from '@/utils/hooks/regions'
import LocationSelector from '@/components/LocationSelector'
import { RegionData } from '@/models/Regions'
import { AccessibleTooltip } from '../AccessibleTooltip'
import SpeechToText from '../SpeechToText'

const MAX_FILES = Number(import.meta.env.VITE_MAX_FILES_PER_UPLOAD) || 15

const PENDING_UPLOAD_SUBMIT_ERROR =
  "Click the Upload button to finish uploading or remove what's listed above before saving."

const LIST_REFRESH_FAILED_ERROR =
  "Your changes were saved, but we couldn't refresh your funding requests. Check your connection, then click Save Changes again."

interface FundingNeedsFormProps {
  orgUuid: UUID | null
  token: string
  mode: 'add' | 'edit'
  fundingNeed?: FundingNeedResponse
  prefillDraft?: FundingNeedDraft | null
  setPrefillDraft?: (draft: FundingNeedDraft | null) => void
  onSuccess?: () => void
  // Called after a successful delete (edit mode). Falls back to onSuccess
  // when not given, so a parent that treats onSuccess as "saved" should pass
  // this to avoid reporting a delete as a save.
  onDeleted?: () => void
  onCancel?: () => void
  org_accepts_gambling_proceeds: boolean
  org_accepts_international: boolean
  org_need_only_context?: boolean
  onFormStateChange?: (hasChanges: boolean) => void
  onPendingUploadChange?: (hasPendingUpload: boolean) => void
  org_region_ids: number[]
  orgWebsite?: string | null
}

// ref for the Cancel and Go back button
export interface FundingNeedsFormRef {
  handleCancel: () => void
}

interface FormErrors {
  [key: string]: string
}

type FormDataState = {
  title: string
  min_amount: string
  amount: string
  description: string
  region_ids: number[]
  iwi: number[]
  // null = inherit from org settings
  accepts_gambling_proceeds: boolean | null
  accepts_international: boolean | null
  use_need_context_only: boolean
  description_part_one?: string
  description_part_two?: string
  description_part_three?: string
  description_part_four?: string
  description_part_five?: string
  uses_org_regions: boolean
}

const descriptionPartQuestions = {
  part_one_q: 'What is this funding for?',
  part_one_a: 'What this funding is for',
  part_two_q: 'What problem does it solve or need does it address?',
  part_two_a: 'What problem this funding addresses',
  part_three_q:
    'What impact or change will this create for your community/kaupapa or on your organisation?',
  part_three_a: 'What impact this funding will have',
  part_four_q: 'Who benefits from this work/mahi?',
  part_four_a: 'Who will benefit from this funding',
  part_five_q: 'Anything else you think is important for funders to know',
  part_five_a: 'Other important information',
}

interface ParsedDescriptionSections {
  part_one: string
  part_two: string
  part_three: string
  part_four: string
  part_five: string
}

function parseDescriptionSections(
  description: string,
): ParsedDescriptionSections | null {
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const t1 = escape(descriptionPartQuestions.part_one_a + ':')
  const t2 = escape(descriptionPartQuestions.part_two_a + ':')
  const t3 = escape(descriptionPartQuestions.part_three_a + ':')
  const t4 = escape(descriptionPartQuestions.part_four_a + ':')
  const t5 = escape(descriptionPartQuestions.part_five_a + ':')

  const pattern = new RegExp(
    `${t1}\\s*(?<part_one>[\\s\\S]*?)` +
      `${t2}\\s*(?<part_two>[\\s\\S]*?)` +
      `${t3}\\s*(?<part_three>[\\s\\S]*?)` +
      `${t4}\\s*(?<part_four>[\\s\\S]*?)` +
      `(?:${t5}\\s*(?<part_five>[\\s\\S]*))?$`,
  )

  const match = pattern.exec(description)
  if (!match?.groups) return null

  return {
    part_one: match.groups.part_one.trim(),
    part_two: match.groups.part_two.trim(),
    part_three: match.groups.part_three.trim(),
    part_four: match.groups.part_four.trim(),
    part_five: (match.groups.part_five ?? '').trim(),
  }
}

interface SuggestionBlockProps {
  question?: string
  descExample?: string
  isInserted: boolean
  onInsert: () => void
}

function SuggestionBlock({
  question,
  descExample,
  isInserted,
  onInsert,
}: SuggestionBlockProps) {
  if (!question && !descExample) return null

  return (
    <Box
      sx={{
        borderLeft: '3px solid #1976d2',
        backgroundColor: '#f5f9ff',
        pl: 2,
        pr: 1,
        py: 1.5,
        borderRadius: 1,
        mt: 1,
        mb: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        <AutoAwesomeIcon sx={{ fontSize: 14, color: '#1976d2' }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          Suggestion
        </Typography>
      </Box>
      {question && (
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: '#1C3045', mt: 1 }}
        >
          {question}
        </Typography>
      )}
      {descExample && (
        <Alert
          severity="info"
          icon={false}
          sx={{ mb: 1, mt: 0.5, backgroundColor: '#fff' }}
          action={
            <Button color="inherit" size="small" onClick={onInsert}>
              {isInserted ? (
                <>
                  <CheckIcon
                    sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }}
                  />
                  Added
                </>
              ) : (
                'Insert'
              )}
            </Button>
          }
        >
          <Typography variant="body2">
            <span style={{ fontWeight: 600 }}>Consider adding:</span> &ldquo;
            {descExample}&rdquo;
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

const FundingNeedsForm = forwardRef<FundingNeedsFormRef, FundingNeedsFormProps>(
  (
    {
      orgUuid,
      token,
      mode,
      fundingNeed,
      // Edit mode never passes this; default to the "no draft" value so the
      // `prefillDraft !== null` checks below don't read a missing prop as an
      // applied draft.
      prefillDraft = null,
      setPrefillDraft,
      onSuccess,
      onDeleted,
      onCancel,
      org_accepts_gambling_proceeds,
      org_accepts_international,
      org_need_only_context,
      onFormStateChange,
      onPendingUploadChange,
      org_region_ids,
      orgWebsite,
    },
    ref,
  ) => {
    const navigate = useNavigate()
    const lastEditNeedIdRef = useRef<number | null>(null)

    useEffect(() => {
      // Scroll to top when component mounts
      window.scrollTo(0, 0)
    }, [])

    // Use appropriate hooks based on mode
    const { createFundingNeed, updateFundingNeed, deleteFundingNeed } =
      useFundingNeeds(token, orgUuid)
    const queryClient = useQueryClient()

    // Create a temporary ID for the funding need (used for uploads in add mode)
    const [tempFundingNeedId, setTempFundingNeedId] = useState<number | null>(
      mode === 'edit' && fundingNeed ? fundingNeed.id : null,
    )

    // Processing state
    const [isProcessing, setIsProcessing] = useState(false)
    const [processingMessage, setProcessingMessage] = useState<string>('')

    // Assessment state
    const [fundingNeedAssessment, setFundingNeedAssessment] =
      useState<FundingNeedDataAssessmentResponse | null>(null)
    const [assessmentFormSnapshot, setAssessmentFormSnapshot] =
      useState<FormDataState | null>(null)

    // Whether the existing description has been parsed into named sections (edit mode only)
    const [descriptionHasSections, setDescriptionHasSections] = useState(false)

    // Hook for document uploads
    const fundingNeedDocs = useFundingNeedDocs(
      orgUuid as UUID,
      token,
      tempFundingNeedId || -1,
    )

    const { isOnboarding } = useOnboardingStatus()

    const baseInitialFormData = useMemo<FormDataState>(() => {
      if (mode === 'edit' && fundingNeed) {
        const parsedSections = parseDescriptionSections(fundingNeed.description)
        return {
          title: fundingNeed.title,
          min_amount: fundingNeed.min_amount ? `${fundingNeed.min_amount}` : '',
          amount: fundingNeed.amount ? `${fundingNeed.amount}` : '',
          description: fundingNeed.description,
          region_ids: fundingNeed.region_ids || [],
          iwi: fundingNeed.iwi || [],
          // null = inherit from org. Kept as-is so an untouched save preserves NULL in the DB.
          accepts_gambling_proceeds: fundingNeed.accepts_gambling_proceeds,
          accepts_international: fundingNeed.accepts_international,
          use_need_context_only: fundingNeed.use_need_context_only || false,
          uses_org_regions: fundingNeed.uses_org_regions || false,
          ...(parsedSections && {
            description_part_one: parsedSections.part_one,
            description_part_two: parsedSections.part_two,
            description_part_three: parsedSections.part_three,
            description_part_four: parsedSections.part_four,
            description_part_five: parsedSections.part_five,
          }),
        }
      }

      return {
        title: '',
        min_amount: '',
        amount: '',
        description: '',
        region_ids: [],
        iwi: [],
        // null = inherit from org. Saving without touching the checkboxes preserves NULL.
        accepts_gambling_proceeds: null,
        accepts_international: null,
        use_need_context_only: false,
        description_part_one: '',
        description_part_two: '',
        description_part_three: '',
        description_part_four: '',
        description_part_five: '',
        uses_org_regions: true,
      }
    }, [
      fundingNeed,
      mode,
      org_accepts_gambling_proceeds,
      org_accepts_international,
    ])

    const [initialFormData, setInitialFormData] =
      useState<FormDataState>(baseInitialFormData)
    const [formData, setFormData] = useState<FormDataState>(baseInitialFormData)
    const [errors, setErrors] = useState<FormErrors>({})
    const [uploadTabValue, setUploadTabValue] = useState(0)
    const [files, setFiles] = useState<File[]>([])
    const [websites, setWebsites] = useState<string[]>([])
    // Set by the standalone upload widgets (edit mode only, once the funding
    // need already exists) when they have files/URLs staged but not yet
    // uploaded — their own "Upload" button is separate from Save Changes
    // below, so Save Changes is disabled meanwhile to avoid silently losing
    // the staged content (#1465). Tracked separately since only one of the
    // two widgets is mounted at a time (the Files/Website URLs tabs).
    const [hasPendingFiles, setHasPendingFiles] = useState(false)
    const [hasPendingWebsites, setHasPendingWebsites] = useState(false)
    const hasPendingUpload = hasPendingFiles || hasPendingWebsites

    // In-app navigation (elsewhere in the app, not refresh/close/back) is
    // guarded by a single useBlocker owned by FundingNeedsList, since a
    // router only honours one active blocker - two nested ones means the
    // outer one wins and this component's own blocker would silently never
    // run. Report the pending-upload flag upward instead; cleared on
    // unmount so a closed form can't leave the list thinking content is
    // still staged.
    //
    // onPendingUploadChange is a fresh inline callback each render at every
    // call site today, so it's currently harmless in the dependency array -
    // but keeping it in a ref and depending only on the value avoids the
    // effect re-running (and re-firing the callback) if a call site ever
    // stops memoizing it.
    const onPendingUploadChangeRef = useRef(onPendingUploadChange)
    onPendingUploadChangeRef.current = onPendingUploadChange
    useEffect(() => {
      onPendingUploadChangeRef.current?.(hasPendingUpload)
      return () => onPendingUploadChangeRef.current?.(false)
    }, [hasPendingUpload])

    useEffect(() => {
      if (!hasPendingUpload) return
      const handler = (e: BeforeUnloadEvent) => e.preventDefault()
      window.addEventListener('beforeunload', handler)
      return () => window.removeEventListener('beforeunload', handler)
    }, [hasPendingUpload])

    // The native-submit guard below sets this specific error while content is
    // staged; once the user resolves that (uploads or removes it) nothing
    // else clears it, so the stale "you must upload or remove it" alert would
    // otherwise sit on screen even after Save re-enables.
    useEffect(() => {
      if (!hasPendingUpload && errors.submit === PENDING_UPLOAD_SUBMIT_ERROR) {
        setErrors((prev) => {
          const { submit: _submit, ...rest } = prev
          return rest
        })
      }
    }, [hasPendingUpload, errors.submit])

    const [showDescriptionWarning, setShowDescriptionWarning] = useState(false)
    const [showLocationWarning, setShowLocationWarning] = useState(false)
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
    const [insertedParts, setInsertedParts] = useState<Set<string>>(new Set())
    const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0)
    const [showRegionInput, setShowRegionInput] = useState(
      !baseInitialFormData.uses_org_regions,
    )

    useEffect(() => {
      if (mode === 'edit') {
        if (!fundingNeed) return
        if (lastEditNeedIdRef.current === fundingNeed.id) {
          return
        }
        lastEditNeedIdRef.current = fundingNeed.id
      } else {
        lastEditNeedIdRef.current = null
      }

      setInitialFormData(baseInitialFormData)
      setFormData(baseInitialFormData)
      setFiles([])
      setWebsites([])
      setErrors({})
      setShowRegionInput(!baseInitialFormData.uses_org_regions)
      setDescriptionHasSections(
        mode === 'edit' && !!baseInitialFormData.description_part_one,
      )
    }, [baseInitialFormData, fundingNeed, mode])

    useEffect(() => {
      if (mode !== 'add' || !prefillDraft) return

      const hasPrefillRegions =
        prefillDraft.region_ids && prefillDraft.region_ids.length > 0

      const prefilledData: FormDataState = {
        ...baseInitialFormData,
        title: prefillDraft.title ?? baseInitialFormData.title,
        min_amount: prefillDraft.min_amount ?? baseInitialFormData.min_amount,
        amount: prefillDraft.amount ?? baseInitialFormData.amount,
        description_part_one:
          prefillDraft.description_part_one ??
          baseInitialFormData.description_part_one,
        description_part_two:
          prefillDraft.description_part_two ??
          baseInitialFormData.description_part_two,
        description_part_three:
          prefillDraft.description_part_three ??
          baseInitialFormData.description_part_three,
        description_part_four:
          prefillDraft.description_part_four ??
          baseInitialFormData.description_part_four,
        description_part_five:
          prefillDraft.description_part_five ??
          baseInitialFormData.description_part_five,
        accepts_gambling_proceeds:
          prefillDraft.accepts_gambling_proceeds ??
          baseInitialFormData.accepts_gambling_proceeds,
        accepts_international:
          prefillDraft.accepts_international ??
          baseInitialFormData.accepts_international,
        region_ids: hasPrefillRegions
          ? prefillDraft.region_ids!
          : baseInitialFormData.region_ids,
        uses_org_regions:
          prefillDraft.uses_org_regions ??
          (hasPrefillRegions ? false : baseInitialFormData.uses_org_regions),
      }

      setInitialFormData(prefilledData)
      setFormData(prefilledData)
      setFiles([])
      setWebsites([])
      setErrors({})
      setShowRegionInput(!prefilledData.uses_org_regions)
    }, [baseInitialFormData, mode, prefillDraft])

    // Fetch regions for the dropdown
    const { regions, isLoading: regionsLoading } = useRegions(token || '')
    const { regionSets, isLoading: regionSetsLoading } = useRegionSets(
      token || '',
    )

    // check if form has been modified from initial state
    const hasFormContent = (): boolean => {
      const hasFormDataChanged =
        formData.title !== initialFormData.title ||
        formData.description !== initialFormData.description ||
        formData.amount !== initialFormData.amount ||
        formData.min_amount !== initialFormData.min_amount ||
        JSON.stringify(formData.region_ids.sort()) !==
          JSON.stringify(initialFormData.region_ids.sort()) ||
        JSON.stringify(formData.iwi.sort()) !==
          JSON.stringify(initialFormData.iwi.sort()) ||
        formData.accepts_gambling_proceeds !==
          initialFormData.accepts_gambling_proceeds ||
        formData.accepts_international !==
          initialFormData.accepts_international ||
        formData.use_need_context_only !==
          initialFormData.use_need_context_only ||
        formData.description_part_one !==
          initialFormData.description_part_one ||
        formData.description_part_two !==
          initialFormData.description_part_two ||
        formData.description_part_three !==
          initialFormData.description_part_three ||
        formData.description_part_four !==
          initialFormData.description_part_four ||
        formData.description_part_five !== initialFormData.description_part_five

      // check if any additional content has been added (files, websites),
      // including content staged in the upload widgets but not yet uploaded
      const hasAdditionalContent =
        files.length > 0 || websites.length > 0 || hasPendingUpload

      return hasFormDataChanged || hasAdditionalContent
    }

    const handleUploadTabChange = (
      _event: React.SyntheticEvent,
      newValue: number,
    ) => {
      setUploadTabValue(newValue)
    }

    const handleAddFile = (newFile: File) => {
      setFiles((prevFiles) => [...prevFiles, newFile])
    }

    // handle cancel confirmation, checks if form has any content or a prefill draft is active
    function handleCancel() {
      // Check initialFormData directly as prefillDraft prop may have become null
      // after a draft was applied (e.g. activePrefill cleared in parent)
      const hasAppliedDraftData =
        mode === 'add' &&
        (initialFormData.title !== '' ||
          (initialFormData.description_part_one ?? '') !== '')
      if (hasFormContent() || prefillDraft !== null || hasAppliedDraftData) {
        setShowCancelConfirmation(true)
      } else {
        onCancel?.()
      }
    }

    // Notify parent when form state changes
    useEffect(() => {
      onFormStateChange?.(hasFormContent())
    }, [formData, files, websites, hasPendingUpload, onFormStateChange])

    // Expose handleCancel method to parent via ref
    useImperativeHandle(ref, () => ({
      handleCancel,
    }))

    const handleRemoveFile = (index: number) => {
      setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
    }

    const handleAddWebsite = (url: string) => {
      setWebsites((prevWebsites) => [...prevWebsites, url])
    }

    const handleRemoveWebsite = (index: number) => {
      setWebsites((prevWebsites) => prevWebsites.filter((_, i) => i !== index))
    }

    // Function to normalize amount input by removing currency symbols and commas
    const normalizeAmount = (amount: string): string => {
      // Remove currency symbols, commas, and whitespace
      return amount.replace(/[$,\s]/g, '')
    }

    const validateForm = (): boolean => {
      const newErrors: FormErrors = {}

      if (!formData.title?.trim()) {
        newErrors.title = 'Title is required'
      }

      if (formData.amount) {
        // Normalize the amount before validation
        const normalizedAmount = normalizeAmount(formData.amount)
        const amountNum = Number(normalizedAmount)

        if (isNaN(amountNum) || amountNum <= 0) {
          newErrors.amount = 'Please enter a valid positive number'
        }
      }

      if (formData.min_amount) {
        // Normalize the min amount before validation
        const normalizedAmount = normalizeAmount(formData.min_amount)
        const amountNum = Number(normalizedAmount)

        if (isNaN(amountNum) || amountNum <= 0) {
          newErrors.min_amount = 'Please enter a valid positive number'
        }
      }

      // Check if min_amount is greater than amount when both are provided
      if (formData.min_amount && formData.amount) {
        const normalizedMinAmount = Number(normalizeAmount(formData.min_amount))
        const normalizedAmount = Number(normalizeAmount(formData.amount))

        if (
          !isNaN(normalizedMinAmount) &&
          !isNaN(normalizedAmount) &&
          normalizedMinAmount > normalizedAmount
        ) {
          newErrors.min_amount =
            'Minimum amount cannot be greater than the full amount'
        }
      }

      if (
        mode === 'edit' &&
        !descriptionHasSections &&
        !formData.description?.trim()
      ) {
        newErrors.description = 'Description is required'
      }

      if (mode === 'add' || (mode === 'edit' && descriptionHasSections)) {
        const requiredParts = [
          {
            key: 'description_part_one',
            q: descriptionPartQuestions.part_one_q,
          },
          {
            key: 'description_part_two',
            q: descriptionPartQuestions.part_two_q,
          },
          {
            key: 'description_part_three',
            q: descriptionPartQuestions.part_three_q,
          },
          {
            key: 'description_part_four',
            q: descriptionPartQuestions.part_four_q,
          },
        ] as const

        for (const part of requiredParts) {
          if (!formData[part.key]?.trim()) {
            newErrors[part.key] = `Description on '${part.q}' is required`
          }
        }
      }

      setErrors(newErrors)

      // scroll to first error if any exist
      if (Object.keys(newErrors).length > 0) {
        // order of fields as they appear in the form
        const fieldOrder = [
          'title',
          'min_amount',
          'amount',
          'regions',
          'description',
          'description_part_one',
          'description_part_two',
          'description_part_three',
          'description_part_four',
          'description_part_five',
        ]

        // scroll to first field with an error based on form order
        const firstErrorKey = fieldOrder.find((field) => newErrors[field])

        if (firstErrorKey) {
          const errorElement = document.getElementById(firstErrorKey)

          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
            // delay to ensure scroll completes before focus
            setTimeout(() => {
              errorElement.focus()
            }, 300)
          }
        }
      }

      return Object.keys(newErrors).length === 0
    }

    const handleDelete = async () => {
      if (mode !== 'edit' || !fundingNeed) return

      // Show confirmation dialog
      const confirmed = window.confirm(
        'Are you sure you want to delete this funding need? This action cannot be undone.',
      )

      if (!confirmed) return

      try {
        await deleteFundingNeed.mutateAsync(
          {
            token,
            fundingNeedId: fundingNeed.id,
          },
          {
            onSuccess: () => {
              ;(onDeleted ?? onSuccess)?.()
            },
            onError: () => {
              setErrors({
                submit: 'Failed to delete funding need. Please try again.',
              })
            },
          },
        )
      } catch {
        setErrors({
          submit: 'Failed to delete funding need. Please try again.',
        })
      }
    }

    const handleSubmit = async (
      e: React.FormEvent,
      force: boolean,
      ignoreAssessment?: boolean,
      forceLocation?: boolean,
    ) => {
      e.preventDefault()

      // Belt-and-braces alongside the disabled Save/Skip-all buttons (#1465):
      // pressing Enter in a text field fires this handler directly via the
      // form's native onSubmit, bypassing whichever button is disabled.
      if (hasPendingUpload) {
        setErrors({ submit: PENDING_UPLOAD_SUBMIT_ERROR })
        return
      }

      if (!validateForm() || !orgUuid) {
        return
      }
      setFundingNeedAssessment(null)
      setIsProcessing(true)
      setProcessingMessage('Saving funding request...')

      // Create a copy of formData for submission to avoid mutating state
      const dataToSubmit = { ...formData }

      // Clear funding need region_ids if using org regions
      if (dataToSubmit.uses_org_regions) {
        dataToSubmit.region_ids = []
      }

      // build description
      if (mode === 'add' || (mode === 'edit' && descriptionHasSections)) {
        const descriptionParts = [
          `${descriptionPartQuestions.part_one_a}:\n${dataToSubmit.description_part_one}`,
          `${descriptionPartQuestions.part_two_a}:\n${dataToSubmit.description_part_two}`,
          `${descriptionPartQuestions.part_three_a}:\n${dataToSubmit.description_part_three}`,
          `${descriptionPartQuestions.part_four_a}:\n${dataToSubmit.description_part_four}`,
        ]

        if (dataToSubmit.description_part_five?.trim()) {
          descriptionParts.push(
            `${descriptionPartQuestions.part_five_a}:\n${dataToSubmit.description_part_five}`,
          )
        }

        dataToSubmit.description = descriptionParts.join('\n\n')
      }

      // char count without spaces
      const textLength = dataToSubmit.description.replace(/\s+/g, '').length

      if (textLength < 300 && !force) {
        setShowDescriptionWarning(true)
        setIsProcessing(false)
        return
      }

      // Check if only "New Zealand" is selected when using specific regions
      if (
        !forceLocation &&
        !dataToSubmit.uses_org_regions &&
        formData.region_ids.length === 1
      ) {
        const selectedRegion = regions?.find(
          (r: RegionData) => r.id === formData.region_ids[0],
        )
        if (
          selectedRegion &&
          selectedRegion.name.toLowerCase() === 'new zealand'
        ) {
          setShowLocationWarning(true)
          setIsProcessing(false)
          return
        }
      }

      try {
        // Normalize amount before submission if it exists
        const normalizedAmount = dataToSubmit.amount
          ? normalizeAmount(dataToSubmit.amount)
          : dataToSubmit.amount || undefined

        const normalizedMinAmount = dataToSubmit.min_amount
          ? normalizeAmount(dataToSubmit.min_amount)
          : dataToSubmit.min_amount || undefined

        // Always use org iwi - send null to backend
        const iwiToSave = null

        if (mode === 'add') {
          // Adding a new funding need
          const fundingNeed: FundingNeedCreate = {
            org_uuid: orgUuid,
            title: dataToSubmit.title || '',
            description: dataToSubmit.description || '',
            min_amount: normalizedMinAmount as string,
            amount: normalizedAmount as string,
            timing_criteria: null,
            region_ids: formData.region_ids || [],
            iwi: iwiToSave,
            accepts_gambling_proceeds: formData.accepts_gambling_proceeds,
            accepts_international: formData.accepts_international,
            use_need_context_only: formData.use_need_context_only,
            force: force || false,
            ignore_assessment: ignoreAssessment || false,
            funding_ideas_session_id: prefillDraft?.funding_ideas_session_id,
          }

          const response = await createFundingNeed.mutateAsync({
            token,
            fundingNeed,
          })

          // Check if response is an assessment response or a created funding need
          if ('Quality' in response) {
            // assessment is returned if data quality is not good enough to create funding need
            setFundingNeedAssessment(response)
            setInsertedParts(new Set())
            setCurrentSuggestionIndex(0)
            setAssessmentFormSnapshot({ ...formData })

            setIsProcessing(false)

            // Focus wizard title for accessibility
            setTimeout(() => {
              const titleElement = document.getElementById(
                'funding-need-suggestion-wizard-title',
              )
              if (titleElement) {
                titleElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
                titleElement.focus()
              }
            }, 100)

            return
          }

          // Store the new funding need ID
          const newFundingNeedId = response.id
          setTempFundingNeedId(newFundingNeedId)

          // Upload files if any are selected
          let uploadSuccess = true
          let errorMessage = ''

          if (files.length > 0) {
            try {
              setProcessingMessage(
                `Analysing ${files.length} document${
                  files.length > 1 ? 's' : ''
                }... This could take some time.`,
              )
              await fundingNeedDocs.uploadFundingNeedDocs.mutateAsync({
                token,
                files,
                orgUuid,
                fundingNeedId: newFundingNeedId,
              })
            } catch {
              uploadSuccess = false
              errorMessage =
                'Failed to upload documents, but funding need was created.'
            }
          }

          // Upload websites if any are selected
          if (websites.length > 0 && uploadSuccess) {
            try {
              setProcessingMessage(
                `Analysing ${websites.length} website${
                  websites.length > 1 ? 's' : ''
                }... This could take some time.`,
              )
              await fundingNeedDocs.uploadFundingNeedWebsites.mutateAsync({
                token,
                websites,
                orgUuid,
                fundingNeedId: newFundingNeedId,
              })
            } catch {
              uploadSuccess = false
              errorMessage =
                'Failed to upload website URLs, but funding need was created.'
            }
          }

          if (uploadSuccess) {
            // Navigate to the match page instead of calling onSuccess
            navigate('/match')
          } else {
            setIsProcessing(false)
            setErrors({
              submit: errorMessage,
            })
          }
        } else if (mode === 'edit' && fundingNeed) {
          // Editing an existing funding need
          const updatedFormData: FundingNeedUpdate = {
            title: dataToSubmit.title,
            min_amount: normalizedMinAmount,
            amount: normalizedAmount,
            description: dataToSubmit.description,
            region_ids: formData.region_ids,
            iwi: iwiToSave,
            accepts_gambling_proceeds: formData.accepts_gambling_proceeds,
            accepts_international: formData.accepts_international,
            use_need_context_only: formData.use_need_context_only,
            force: force || false,
            ignore_assessment: ignoreAssessment || false,
          }

          const response = await updateFundingNeed.mutateAsync({
            token,
            fundingNeedId: fundingNeed.id,
            updates: updatedFormData,
          })

          // Check if response is an assessment response or a created funding need
          if ('Quality' in response) {
            // assessment is returned if data quality is not good enough to create funding need
            setFundingNeedAssessment(response)
            setInsertedParts(new Set())
            setCurrentSuggestionIndex(0)
            setAssessmentFormSnapshot({ ...formData })

            setIsProcessing(false)

            // Focus wizard title for accessibility
            setTimeout(() => {
              const titleElement = document.getElementById(
                'funding-need-suggestion-wizard-title',
              )
              if (titleElement) {
                titleElement.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                })
                titleElement.focus()
              }
            }, 100)

            return
          }

          let uploadSuccess = true
          let errorMessage = ''

          // Handle any new file uploads in edit mode
          if (files.length > 0) {
            try {
              setProcessingMessage(
                `Analysing ${files.length} document${
                  files.length > 1 ? 's' : ''
                }... This could take some time.`,
              )
              await fundingNeedDocs.uploadFundingNeedDocs.mutateAsync({
                token,
                files,
                orgUuid,
                fundingNeedId: fundingNeed.id,
              })
            } catch {
              uploadSuccess = false
              errorMessage =
                'Failed to upload documents, but funding need was updated.'
            }
          }

          // Handle any new website uploads in edit mode
          if (websites.length > 0 && uploadSuccess) {
            try {
              setProcessingMessage(
                `Analysing ${websites.length} website${
                  websites.length > 1 ? 's' : ''
                }... This could take some time.`,
              )
              await fundingNeedDocs.uploadFundingNeedWebsites.mutateAsync({
                token,
                websites,
                orgUuid,
                fundingNeedId: fundingNeed.id,
              })
            } catch {
              uploadSuccess = false
              errorMessage =
                'Failed to upload website URLs, but funding need was updated.'
            }
          }

          if (uploadSuccess) {
            // Wait for the funding-needs list to refetch before collapsing
            // the form: otherwise re-opening Edit immediately reads the
            // still-stale cached need and a second save can resend its old
            // field values, clobbering the edit just made.
            const savedAt = Date.now()
            await queryClient.invalidateQueries({ queryKey: ['fundingNeeds'] })
            // invalidateQueries also resolves when the refetch is paused
            // (offline) or fails, leaving the old values cached. Only collapse
            // once the list really holds data fetched after this save;
            // otherwise keep the form open, since it still holds exactly what
            // was saved and saving again resends those values safely.
            const listState = queryClient.getQueryState([
              'fundingNeeds',
              orgUuid,
            ])
            const listIsFresh =
              !listState ||
              (listState.status === 'success' &&
                listState.dataUpdatedAt >= savedAt)
            if (listIsFresh) {
              onSuccess?.()
            } else {
              setIsProcessing(false)
              setErrors({ submit: LIST_REFRESH_FAILED_ERROR })
            }
          } else {
            setIsProcessing(false)
            setErrors({
              submit: errorMessage,
            })
          }
        }
      } catch (error) {
        setIsProcessing(false)
        if (error instanceof Error) {
          if (
            error.message ===
            'Either a minimum amount or full amount must be provided'
          ) {
            setErrors({
              submit: 'Either a minimum amount or full amount must be provided',
            })
          } else {
            setErrors({
              submit: `Failed to ${
                mode === 'add' ? 'create' : 'update'
              } funding need. Please try again.`,
            })
          }
        } else {
          setErrors({
            submit: `Failed to ${
              mode === 'add' ? 'create' : 'update'
            } funding need. Please try again.`,
          })
        }
      }
    }

    async function handleForceSubmit(e: React.MouseEvent<HTMLButtonElement>) {
      setShowDescriptionWarning(false)
      await handleSubmit(e, true, false, false) // force description, check location
    }

    async function handleForceLocationSubmit(
      e: React.MouseEvent<HTMLButtonElement>,
    ) {
      setShowLocationWarning(false)
      await handleSubmit(e, true, false, true) // force both
    }

    // Determine button text based on mode
    const submitButtonText =
      mode === 'add'
        ? createFundingNeed.isPending
          ? 'Saving...'
          : !isOnboarding
            ? 'Save and Continue'
            : 'Save and Find Matches'
        : updateFundingNeed.isPending
          ? 'Saving...'
          : 'Save Changes'

    return (
      <>
        {/* Processing backdrop */}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            flexDirection: 'column',
            gap: 2,
          }}
          open={isProcessing}
        >
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6">{processingMessage}</Typography>
        </Backdrop>

        {showDescriptionWarning && (
          <Dialog
            open={showDescriptionWarning}
            onClose={() => setShowDescriptionWarning(false)}
            aria-labelledby="warning-dialog-title"
            aria-describedby="warning-dialog-description"
          >
            <DialogTitle id="warning-dialog-title">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src={`/Thumbs-down400px.png`}
                  alt="Thumbs down"
                  style={{
                    width: '40px',
                    height: '40px',
                    marginRight: '20px',
                    marginLeft: '20px',
                    marginTop: '10px',
                  }}
                />
                <Typography variant="h6" sx={{ mt: 1 }}>
                  Your funding request is looking a little lean!
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                id="warning-dialog-description"
                sx={{ color: '#1C3045', mt: 1 }}
              >
                Add more information to the description{' '}
                {mode === 'add' ? 'fields' : ''} to get better funding matches.
              </DialogContentText>

              <Typography
                variant="body1"
                sx={{ color: '#1C3045', mt: 2, mb: 1 }}
              >
                Try answering these questions with as much detail as you can:
              </Typography>

              <Typography variant="body2" sx={{ color: '#1C3045', ml: 1 }}>
                • What is this funding for?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  ml: 2,
                  mb: 1,
                  fontStyle: 'italic',
                }}
              >
                The specific project, programme, or item you need funding for
              </Typography>

              <Typography variant="body2" sx={{ color: '#1C3045', ml: 1 }}>
                • What problem does it solve or need does it address?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  ml: 2,
                  mb: 1,
                  fontStyle: 'italic',
                }}
              >
                The gap, challenge, or need in your community this will address
              </Typography>

              <Typography variant="body2" sx={{ color: '#1C3045', ml: 1 }}>
                • What impact or change will this create for your
                community/kaupapa or organisation?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  ml: 2,
                  mb: 1,
                  fontStyle: 'italic',
                }}
              >
                The short or long-term difference this funding will make
              </Typography>

              <Typography variant="body2" sx={{ color: '#1C3045', ml: 1 }}>
                • Who benefits from this work/mahi?
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  ml: 2,
                  mb: 1,
                  fontStyle: 'italic',
                }}
              >
                The people, groups, or communities who will benefit
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: '#1C3045', ml: 1, mb: 2 }}
              >
                • Optionally, add anything else you think is important for
                funders to know
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={(e) => handleForceSubmit(e)}
                variant="text"
                disableRipple
                sx={{
                  textTransform: 'none',
                  width: '150px',
                  '&:focus-visible': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Save and continue
              </Button>

              <Button
                onClick={() => setShowDescriptionWarning(false)}
                variant="outlined"
                disableRipple
                sx={{
                  color: 'white',
                  bgcolor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  width: '150px',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Keep editing
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {showLocationWarning && (
          <Dialog
            open={showLocationWarning}
            onClose={() => setShowLocationWarning(false)}
            aria-labelledby="location-warning-dialog-title"
            aria-describedby="location-warning-dialog-description"
          >
            <DialogTitle id="location-warning-dialog-title">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  Are you sure this is a national funding request?
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                id="location-warning-dialog-description"
                sx={{
                  color: '#1C3045',
                  mt: 1,
                }}
              >
                You've only selected "New Zealand" as the location for this
                funding request. If it's for specific towns, cities, or regions,
                you'll miss out on local funding opportunities.
              </DialogContentText>

              <Typography
                variant="body2"
                sx={{ color: '#1C3045', mt: 2, mb: 1 }}
              >
                Adding specific locations helps you get matched with:
              </Typography>

              <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
                • Local council grants
              </Typography>
              <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
                • Regional community funding
              </Typography>
              <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
                • Place-based initiatives
              </Typography>
              <Typography variant="body2" sx={{ color: '#1C3045', ml: 2 }}>
                • Local trust funding
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: '#1C3045', mt: 2, fontStyle: 'italic' }}
              >
                Don't worry — adding specific locations won't remove national
                opportunities. You'll still see everything available for New
                Zealand.
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={(e) => handleForceLocationSubmit(e)}
                variant="text"
                disableRipple
                sx={{
                  textTransform: 'none',
                  width: '200px',
                  '&:focus-visible': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Yes, it's national only
              </Button>

              <Button
                onClick={() => setShowLocationWarning(false)}
                variant="contained"
                disableRipple
                sx={{
                  color: 'white',
                  bgcolor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  width: '150px',
                }}
              >
                Add locations
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <Box
          component="form"
          onSubmit={(e) => handleSubmit(e, false)}
          sx={{
            width: '100%',
            maxWidth: 800,
            mx: 'auto',
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            bgcolor: 'white',
            boxShadow: mode === 'add' ? 'none' : 1,
            border: mode === 'add' ? '1px solid #e0e0e0' : 'none',
            '& .MuiFormControl-root': {
              textAlign: 'left',
            },
          }}
        >
          {mode === 'add' && (
            <Typography
              variant="h5"
              component="h2"
              sx={{ mb: 3, fontWeight: 600, color: '#333', textAlign: 'left' }}
            >
              Add a Funding Request below {isOnboarding && 'to get started'}
            </Typography>
          )}

          <FormControl fullWidth sx={{ mb: 4 }}>
            <FormLabel
              htmlFor="title"
              sx={{ fontWeight: 600, mb: 1, color: '#333' }}
            >
              Title
            </FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A name to describe what this request is for
            </Typography>
            <TextField
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              error={!!errors.title}
              helperText={errors.title}
              placeholder="Enter a title for your funding request"
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#757575',
                },
              }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 4 }}>
            <FormLabel
              htmlFor="amount"
              sx={{ fontWeight: 600, mb: 1, color: '#333' }}
            >
              Amount Needed
            </FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can include dollar signs ($) and commas (e.g., $1,000)
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                mt: 1,
                gap: 3,
                '& > *': { flexBasis: { xs: '100%', sm: '50%' } },
              }}
            >
              <Box sx={{ width: '100%' }}>
                <FormLabel
                  sx={{ fontWeight: 500, color: '#555', fontSize: '14px' }}
                  htmlFor="min_amount"
                >
                  Minimum Amount (optional)
                </FormLabel>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  The minimum contribution amount you would accept
                </Typography>
                <TextField
                  id="min_amount"
                  type="text"
                  value={formData.min_amount}
                  onChange={(e) => {
                    const value = e.target.value
                    setFormData({ ...formData, min_amount: value })
                  }}
                  error={!!errors.min_amount}
                  helperText={errors.min_amount}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <FormLabel
                  sx={{ fontWeight: 500, color: '#555', fontSize: '14px' }}
                  htmlFor="amount"
                >
                  Full Amount Needed
                </FormLabel>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  The full amount of funding you need
                </Typography>
                <TextField
                  id="amount"
                  type="text"
                  value={formData.amount}
                  onChange={(e) => {
                    const value = e.target.value
                    setFormData({ ...formData, amount: value })
                  }}
                  error={!!errors.amount}
                  helperText={errors.amount}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 4 }}>
            <FormLabel
              htmlFor="regions"
              sx={{ fontWeight: 600, mb: 1, color: '#333' }}
            >
              Locations
            </FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select the location(s) where the funding will be used.
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.uses_org_regions}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setFormData({
                      ...formData,
                      uses_org_regions: checked,
                      // When switching to need-specific locations, seed the picker
                      // with the org's locations so it opens with them in place and
                      // the user can add/remove from there. When reverting to org
                      // locations, clear the need-specific list.
                      region_ids: checked ? [] : [...(org_region_ids ?? [])],
                    })
                    setShowRegionInput(!checked)
                  }}
                  sx={{
                    '&.MuiCheckbox-colorPrimary': {
                      color: '#1C3045',
                    },
                  }}
                  key={'use_org_regions'}
                />
              }
              label="Use my organisation's locations"
            />
            {!showRegionInput && (
              <Box sx={{ ml: 4, my: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Untick to set different locations for this funding need only.
                </Typography>
              </Box>
            )}
            {showRegionInput && (
              <LocationSelector
                regions={regions ?? []}
                regionSets={regionSets ?? []}
                selectedIds={formData.region_ids ?? []}
                onChange={(ids) =>
                  setFormData({ ...formData, region_ids: ids })
                }
                mode="settings"
              />
            )}
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              mb: 3,
              mt: 5,
            }}
          >
            <FormLabel
              htmlFor="match_settings"
              sx={{ fontWeight: 600, mb: 1, color: '#333' }}
            >
              Accepting Gambling Proceeds
            </FormLabel>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              If applicable, you can update the settings for Accepting Gambling
              Proceeds for this funding request. <br />
              The defaults are what you have set in your Organisation Details.
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        !(
                          formData.accepts_gambling_proceeds ??
                          org_accepts_gambling_proceeds
                        )
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          accepts_gambling_proceeds: !e.target.checked,
                        })
                      }
                      sx={{
                        '&.MuiCheckbox-colorPrimary': {
                          color: '#1C3045',
                        },
                      }}
                      key={'accepts_gambling_proceeds'}
                    />
                  }
                  label="Refuse Gambling Proceeds"
                />
                <AccessibleTooltip title="Proceeds from Lotteries, Pokies, and Casinos">
                  <span
                    role="img"
                    aria-label="Info: Proceeds from Lotteries, Pokies, and Casinos"
                    tabIndex={0}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: '20px', ml: -1, color: 'gray' }}
                    />
                  </span>
                </AccessibleTooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 10 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        formData.accepts_international ??
                        org_accepts_international
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          accepts_international: e.target.checked,
                        })
                      }
                      sx={{
                        '&.MuiCheckbox-colorPrimary': {
                          color: '#1C3045',
                        },
                      }}
                      key={'accepts_international'}
                    />
                  }
                  label="Accept International Funding"
                />
                <AccessibleTooltip title="Funding Opportunities from Outside of New Zealand">
                  <span
                    role="img"
                    aria-label="Info: Funding Opportunities from Outside of New Zealand"
                    tabIndex={0}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: '20px', ml: -1, color: 'gray' }}
                    />
                  </span>
                </AccessibleTooltip>
              </Box>
            </Box>

            {/* Need Only Context Checkbox - Only show when org has the feature */}
            {org_need_only_context && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.use_need_context_only}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          use_need_context_only: e.target.checked,
                        })
                      }
                      sx={{
                        '&.MuiCheckbox-colorPrimary': {
                          color: '#1C3045',
                        },
                      }}
                      key={'use_need_context_only'}
                    />
                  }
                  label="Only use context from this funding need to draft applications"
                />
                <AccessibleTooltip title="When enabled, applications will be drafted using only the information from this specific funding need, excluding organisation-level profile data">
                  <span
                    role="img"
                    aria-label="Info: When enabled, applications will be drafted using only the information from this specific funding need, excluding organisation-level profile data"
                    tabIndex={0}
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: '20px', ml: 1, color: 'gray' }}
                    />
                  </span>
                </AccessibleTooltip>
              </Box>
            )}
          </Box>

          <FormControl fullWidth sx={{ mb: 4 }}>
            <FormLabel
              htmlFor="description"
              sx={{ fontWeight: 600, mb: 1, color: '#333' }}
            >
              Description
            </FormLabel>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              What is the funding for? Why does it matter? What impact will this
              have on your community/kaupapa or on your organisation? Who
              benefits from this service, and anything else you think is
              important to add.
            </Typography>
            {mode === 'add' || descriptionHasSections ? (
              <Box>
                <FormLabel
                  htmlFor="description_part_one"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    color: '#555',
                    display: 'block',
                    mt: 2,
                  }}
                >
                  {descriptionPartQuestions.part_one_q}
                </FormLabel>
                <Box>
                  <SpeechToText
                    token={token}
                    onTranscription={(text) =>
                      setFormData((prev) => ({
                        ...prev,
                        description_part_one: prev.description_part_one
                          ? `${prev.description_part_one}\n\n${text}`
                          : text,
                      }))
                    }
                  />
                  <TextField
                    id="description_part_one"
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={formData.description_part_one || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description_part_one: e.target.value,
                      })
                    }
                    placeholder="Describe what this funding will be used for"
                    error={!!errors.description_part_one}
                    helperText={errors.description_part_one}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: '#fff',
                        overflowWrap: 'break-word',
                        wordWrap: 'break-word',
                        resize: 'vertical',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                  />
                </Box>

                <FormLabel
                  htmlFor="description_part_two"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    color: '#555',
                    display: 'block',
                    mt: 3,
                  }}
                >
                  {descriptionPartQuestions.part_two_q}
                </FormLabel>
                <Box>
                  <SpeechToText
                    token={token}
                    onTranscription={(text) =>
                      setFormData((prev) => ({
                        ...prev,
                        description_part_two: prev.description_part_two
                          ? `${prev.description_part_two}\n\n${text}`
                          : text,
                      }))
                    }
                  />
                  <TextField
                    id="description_part_two"
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={formData.description_part_two || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description_part_two: e.target.value,
                      })
                    }
                    placeholder="Explain the problem or need this funding addresses"
                    error={!!errors.description_part_two}
                    helperText={errors.description_part_two}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: '#fff',
                        overflowWrap: 'break-word',
                        wordWrap: 'break-word',
                        resize: 'vertical',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                  />
                </Box>

                <FormLabel
                  htmlFor="description_part_three"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    color: '#555',
                    display: 'block',
                    mt: 3,
                  }}
                >
                  {descriptionPartQuestions.part_three_q}
                </FormLabel>
                <Box>
                  <SpeechToText
                    token={token}
                    onTranscription={(text) =>
                      setFormData((prev) => ({
                        ...prev,
                        description_part_three: prev.description_part_three
                          ? `${prev.description_part_three}\n\n${text}`
                          : text,
                      }))
                    }
                  />
                  <TextField
                    id="description_part_three"
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={formData.description_part_three || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description_part_three: e.target.value,
                      })
                    }
                    placeholder="Describe the impact or change this will create"
                    error={!!errors.description_part_three}
                    helperText={errors.description_part_three}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: '#fff',
                        overflowWrap: 'break-word',
                        wordWrap: 'break-word',
                        resize: 'vertical',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                  />
                </Box>

                <FormLabel
                  htmlFor="description_part_four"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    color: '#555',
                    display: 'block',
                    mt: 3,
                  }}
                >
                  {descriptionPartQuestions.part_four_q}
                </FormLabel>
                <Box>
                  <SpeechToText
                    token={token}
                    onTranscription={(text) =>
                      setFormData((prev) => ({
                        ...prev,
                        description_part_four: prev.description_part_four
                          ? `${prev.description_part_four}\n\n${text}`
                          : text,
                      }))
                    }
                  />
                  <TextField
                    id="description_part_four"
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={formData.description_part_four || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description_part_four: e.target.value,
                      })
                    }
                    placeholder="Describe who will benefit from this work"
                    error={!!errors.description_part_four}
                    helperText={errors.description_part_four}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: '#fff',
                        overflowWrap: 'break-word',
                        wordWrap: 'break-word',
                        resize: 'vertical',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                  />
                </Box>

                <FormLabel
                  htmlFor="description_part_five"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    color: '#555',
                    display: 'block',
                    mt: 3,
                  }}
                >
                  {descriptionPartQuestions.part_five_q} (Optional)
                </FormLabel>
                <Box>
                  <SpeechToText
                    token={token}
                    onTranscription={(text) =>
                      setFormData((prev) => ({
                        ...prev,
                        description_part_five: prev.description_part_five
                          ? `${prev.description_part_five}\n\n${text}`
                          : text,
                      }))
                    }
                  />
                  <TextField
                    id="description_part_five"
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={formData.description_part_five || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        description_part_five: e.target.value,
                      })
                    }
                    placeholder="Add any additional information you think is important"
                    error={!!errors.description_part_five}
                    helperText={errors.description_part_five}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1,
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: '#fff',
                        overflowWrap: 'break-word',
                        wordWrap: 'break-word',
                        resize: 'vertical',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Box>
                <SpeechToText
                  token={token}
                  onTranscription={(text) =>
                    setFormData({
                      ...formData,
                      description: formData.description
                        ? `${formData.description}\n\n${text}`
                        : text,
                    })
                  }
                />
                <TextField
                  id="description"
                  multiline
                  minRows={6}
                  maxRows={14}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter a detailed description of your funding request"
                  error={!!errors.description}
                  helperText={errors.description}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                    '& .MuiOutlinedInput-input': {
                      backgroundColor: '#fff',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                />
              </Box>
            )}
          </FormControl>

          {/* File/Document Upload Section - Only shown in Add mode or when editing a specific funding need */}
          {(mode === 'add' || (mode === 'edit' && tempFundingNeedId)) && (
            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
                Supporting Documents (Optional)
              </FormLabel>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You can add files or website links that provide more details
                about your funding request. These will be included when you{' '}
                {mode === 'add' ? 'create' : 'update'} this funding request.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Note: If you have already uploaded a resource to your
                organisation profile, you don’t need to add it here.
              </Typography>
              <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={uploadTabValue}
                    onChange={handleUploadTabChange}
                    aria-label="document upload tabs"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        minHeight: '48px',
                      },
                    }}
                  >
                    <Tab
                      disableRipple
                      disabled={hasPendingWebsites}
                      icon={<UploadFileIcon sx={{ mr: 1 }} />}
                      label="Files"
                      iconPosition="start"
                      aria-describedby={
                        hasPendingUpload
                          ? 'funding-need-upload-tab-pending-caption'
                          : undefined
                      }
                    />
                    <Tab
                      disableRipple
                      disabled={hasPendingFiles}
                      icon={<LanguageIcon sx={{ mr: 1 }} />}
                      label="Website URLs"
                      iconPosition="start"
                      aria-describedby={
                        hasPendingUpload
                          ? 'funding-need-upload-tab-pending-caption'
                          : undefined
                      }
                    />
                  </Tabs>
                </Box>
                {hasPendingUpload && (
                  // Warning register, not error: nothing has gone wrong, an
                  // action is outstanding. Error red is reserved for the
                  // destructive Delete button and real failures (#1465).
                  <Box
                    sx={(theme) => ({
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1,
                      // Same derivation MUI uses for a standard warning Alert.
                      // warning.dark on white was only 3.8:1 and failed WCAG AA
                      // at caption size; this pairing is ~8.7:1 (#1465).
                      bgcolor: lighten(theme.palette.warning.light, 0.9),
                      color: darken(theme.palette.warning.light, 0.6),
                      border: `1px solid ${lighten(
                        theme.palette.warning.light,
                        0.7,
                      )}`,
                    })}
                  >
                    <WarningAmberOutlinedIcon
                      fontSize="small"
                      sx={{ flexShrink: 0 }}
                    />
                    <Typography
                      id="funding-need-upload-tab-pending-caption"
                      role="status"
                      variant="caption"
                      sx={{ color: 'inherit' }}
                    >
                      {hasPendingFiles
                        ? "Your files aren't uploaded yet — upload or remove them. Switching to upload websites now would lose them."
                        : "Your website URLs aren't uploaded yet — upload or remove them. Switching to upload files now would lose them."}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    mt: 2,
                    border: '1px solid #eee',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  {uploadTabValue === 0 ? (
                    <Box sx={{ width: '100%' }}>
                      {mode === 'edit' && tempFundingNeedId ? (
                        <UploadFundingNeedFile
                          orgUuid={orgUuid as UUID}
                          token={token}
                          fundingNeedId={tempFundingNeedId}
                          onPendingChange={setHasPendingFiles}
                        />
                      ) : (
                        <Box>
                          <div
                            className="dropzone-area"
                            style={{
                              border: '2px dashed #ccc',
                              padding: '20px',
                              borderRadius: '4px',
                              textAlign: 'center',
                              cursor: 'pointer',
                              marginBottom: '16px',
                              backgroundColor: '#f9f9f9',
                            }}
                          >
                            <input
                              type="file"
                              id="fileInput"
                              multiple
                              accept=".pdf,.txt,.docx,.csv"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                if (e.target.files) {
                                  Array.from(e.target.files).forEach((file) => {
                                    handleAddFile(file)
                                  })
                                }
                              }}
                            />
                            <label
                              htmlFor="fileInput"
                              style={{ display: 'block', cursor: 'pointer' }}
                            >
                              <Typography
                                variant="body1"
                                style={{
                                  fontWeight: 500,
                                  marginBottom: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <UploadFileIcon
                                  sx={{
                                    fontSize: 40,
                                    color: '#1976d2',
                                    mr: 1,
                                  }}
                                />
                                Drag and drop files here or click to upload
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Max file size: 25MB. Max files per upload:{' '}
                                {MAX_FILES}. Allowed types: PDF, TXT, DOCX, CSV
                              </Typography>
                            </label>
                          </div>

                          {files.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Selected Files ({files.length})
                              </Typography>
                              {files.map((file, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: '#f5f5f5',
                                  }}
                                >
                                  <Typography variant="body2">
                                    {file.name}
                                  </Typography>
                                  <Button
                                    disableRipple
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveFile(index)}
                                    sx={{
                                      '&:focus': {
                                        backgroundColor: '#e8e8e8',
                                      },
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%' }}>
                      {mode === 'edit' && tempFundingNeedId ? (
                        <UploadFundingNeedWebsiteUrls
                          orgUuid={orgUuid as UUID}
                          token={token}
                          fundingNeedId={tempFundingNeedId}
                          orgWebsite={orgWebsite}
                          onPendingChange={setHasPendingWebsites}
                        />
                      ) : (
                        <Box>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                              Add Website URLs
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mb: 2 }}
                            >
                              You can add website links that contain information
                              relevant to your funding request.
                            </Typography>

                            <Box sx={{ display: 'flex', mb: 2 }}>
                              <TextField
                                fullWidth
                                label="Enter a website URL"
                                placeholder="https://example.com"
                                variant="outlined"
                                id="websiteUrl"
                                sx={{
                                  mr: 1,
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#757575',
                                  },
                                }}
                              />
                              <Button
                                variant="contained"
                                disableRipple
                                onClick={() => {
                                  const input = document.getElementById(
                                    'websiteUrl',
                                  ) as HTMLInputElement
                                  if (input.value) {
                                    const url = input.value
                                    // Add http:// if not present
                                    const urlWithProtocol =
                                      url.startsWith('http://') ||
                                      url.startsWith('https://')
                                        ? url
                                        : `http://${url}`
                                    handleAddWebsite(urlWithProtocol)
                                    input.value = ''
                                  }
                                }}
                              >
                                Add
                              </Button>
                            </Box>
                          </Box>

                          {websites.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Websites to add ({websites.length})
                              </Typography>
                              {websites.map((url, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: '#f5f5f5',
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '400px',
                                    }}
                                  >
                                    {url}
                                  </Typography>
                                  <Button
                                    disableRipple
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveWebsite(index)}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </FormControl>
          )}

          {/* SUGGESTION WIZARD */}
          {(() => {
            const isNonSectioned = mode === 'edit' && !descriptionHasSections
            const activeSuggestions =
              fundingNeedAssessment && fundingNeedAssessment.Quality !== 'Good'
                ? [
                    {
                      partKey: 'desc_part_1',
                      fieldKey: 'description_part_one' as const,
                      label: descriptionPartQuestions.part_one_q,
                      question: fundingNeedAssessment.question_part_1,
                      descExample: fundingNeedAssessment.desc_part_1,
                    },
                    {
                      partKey: 'desc_part_2',
                      fieldKey: 'description_part_two' as const,
                      label: descriptionPartQuestions.part_two_q,
                      question: fundingNeedAssessment.question_part_2,
                      descExample: fundingNeedAssessment.desc_part_2,
                    },
                    {
                      partKey: 'desc_part_3',
                      fieldKey: 'description_part_three' as const,
                      label: descriptionPartQuestions.part_three_q,
                      question: fundingNeedAssessment.question_part_3,
                      descExample: fundingNeedAssessment.desc_part_3,
                    },
                    {
                      partKey: 'desc_part_4',
                      fieldKey: 'description_part_four' as const,
                      label: descriptionPartQuestions.part_four_q,
                      question: fundingNeedAssessment.question_part_4,
                      descExample: fundingNeedAssessment.desc_part_4,
                    },
                    {
                      partKey: 'desc_part_5',
                      fieldKey: 'description_part_five' as const,
                      label: descriptionPartQuestions.part_five_q,
                      question: fundingNeedAssessment.question_part_5,
                      descExample: fundingNeedAssessment.desc_part_5,
                    },
                  ].filter((s) => s.question || s.descExample)
                : []

            if (activeSuggestions.length === 0) return null

            const idx = Math.min(
              currentSuggestionIndex,
              activeSuggestions.length - 1,
            )
            const current = activeSuggestions[idx]
            const isLast = idx === activeSuggestions.length - 1

            const currentFieldValue = isNonSectioned
              ? formData.description
              : formData[current.fieldKey] || ''
            const snapshotFieldValue = assessmentFormSnapshot
              ? isNonSectioned
                ? assessmentFormSnapshot.description
                : assessmentFormSnapshot[current.fieldKey] || ''
              : ''
            const hasFieldChanged = currentFieldValue !== snapshotFieldValue

            return (
              <Box
                role="region"
                aria-labelledby="funding-need-suggestion-wizard-title"
                sx={{
                  mt: 3,
                  mb: 2,
                  border: '1px solid #c2d9f0',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: '#fff',
                }}
              >
                {/* Title */}
                <Typography
                  id="funding-need-suggestion-wizard-title"
                  variant="h6"
                  tabIndex={-1}
                  sx={{ fontWeight: 600, color: '#1C3045', mb: 0.5 }}
                >
                  We need a bit more information from you
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Read through the suggestions below and add more detail to
                  improve your chances with funders.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click <strong>Insert</strong> to add the generated suggestion
                  to your description and/or edit directly in the space below.
                </Typography>
                {activeSuggestions.length > 1 && (
                  <Typography variant="body2" color="text.secondary">
                    Click the <strong>Next</strong> button to move on to the
                    next suggestion.
                  </Typography>
                )}

                {/* Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: '#1976d2' }}
                    >
                      Suggestions ({idx + 1} of {activeSuggestions.length})
                    </Typography>
                  </Box>
                </Box>
                {/* Section label */}
                {/* <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: '#555', mb: 0.5 }}
                >
                  Tell us more about: <br />
                  <span style={{ fontWeight: '400' }}>{current.label}</span>
                </Typography> */}
                {current.question && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500, color: '#555', mb: 2 }}
                  >
                    Try to answer this question: <br />
                    <span style={{ fontWeight: '400' }}>
                      {current.question}
                    </span>
                  </Typography>
                )}

                {/* Suggestion block */}
                <SuggestionBlock
                  question={undefined}
                  descExample={current.descExample}
                  isInserted={insertedParts.has(current.partKey)}
                  onInsert={() => {
                    if (isNonSectioned) {
                      setFormData({
                        ...formData,
                        description:
                          (formData.description
                            ? formData.description + '\n\n'
                            : '') + current.descExample,
                      })
                    } else {
                      setFormData({
                        ...formData,
                        [current.fieldKey]:
                          (formData[current.fieldKey]
                            ? formData[current.fieldKey] + '\n\n'
                            : '') + current.descExample,
                      })
                    }
                    setInsertedParts((prev) =>
                      new Set(prev).add(current.partKey),
                    )
                  }}
                />

                {/* Navigation — rendered before field in non-sectioned mode */}
                {(() => {
                  const navBar = (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2,
                      }}
                    >
                      {idx > 0 ? (
                        <Button
                          size="small"
                          variant="text"
                          disableRipple
                          onClick={() => setCurrentSuggestionIndex(idx - 1)}
                          sx={{ textTransform: 'none', fontSize: '13px' }}
                        >
                          <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} />
                          Previous Suggestion
                        </Button>
                      ) : (
                        <Box />
                      )}
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        {!isLast && (
                          <Button
                            size="small"
                            variant="text"
                            disableRipple
                            onClick={(e) => handleSubmit(e, true, true)}
                            disabled={hasPendingUpload}
                            sx={{
                              color: '#666',
                              textTransform: 'none',
                              fontSize: '13px',
                            }}
                          >
                            Skip all
                          </Button>
                        )}
                        {isLast ? (
                          <Button
                            variant="contained"
                            disableRipple
                            onClick={(e) => handleSubmit(e, true, true)}
                            disabled={hasPendingUpload}
                            sx={{
                              bgcolor: '#1C3045',
                              color: 'white',
                              textTransform: 'none',
                              '&:hover': { bgcolor: '#152433' },
                            }}
                          >
                            Save Changes
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            disableRipple
                            onClick={() => setCurrentSuggestionIndex(idx + 1)}
                            sx={{
                              bgcolor: '#1976d2',
                              color: 'white',
                              textTransform: 'none',
                              '&:hover': { bgcolor: '#1565c0' },
                            }}
                          >
                            {hasFieldChanged ? 'Next' : 'Skip'}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  )

                  const field = (
                    <TextField
                      multiline
                      minRows={4}
                      maxRows={8}
                      fullWidth
                      value={
                        isNonSectioned
                          ? formData.description
                          : formData[current.fieldKey] || ''
                      }
                      onChange={(e) => {
                        if (isNonSectioned) {
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        } else {
                          setFormData({
                            ...formData,
                            [current.fieldKey]: e.target.value,
                          })
                        }
                      }}
                      sx={{
                        mt: 1,
                        '& .MuiOutlinedInput-root': { borderRadius: 1 },
                        '& .MuiOutlinedInput-input': {
                          backgroundColor: '#fff',
                          overflowWrap: 'break-word',
                          wordWrap: 'break-word',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#757575',
                        },
                      }}
                    />
                  )

                  return isNonSectioned ? (
                    <>
                      {navBar}
                      {field}
                    </>
                  ) : (
                    <>
                      {field}
                      {navBar}
                    </>
                  )
                })()}
              </Box>
            )
          })()}

          {errors.submit && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                mt: 1,
                borderRadius: 1,
                '& .MuiAlert-icon': {
                  alignItems: 'center',
                },
              }}
            >
              {errors.submit}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            {mode === 'edit' && (
              <Button
                type="button"
                onClick={handleDelete}
                variant="contained"
                disableRipple
                sx={{
                  bgcolor: '#d32f2f', // Red color for danger
                  color: 'white',
                  px: 3,
                  py: 1,
                  fontSize: '16px',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 1,
                  marginRight: 'auto',
                  '&:hover': {
                    bgcolor: '#9a0007', // Darker red on hover
                  },
                }}
                disabled={deleteFundingNeed?.isPending || isProcessing}
              >
                Delete
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              disableRipple
              onClick={handleCancel}
              sx={{
                mr: 2,
                borderColor: '#1C3045',
                color: '#1C3045',
                px: 3,
                py: 1,
                fontSize: '0.9rem',
                fontWeight: 500,
                textTransform: 'none',
                borderRadius: 1,
                border: 1,
                display: !isOnboarding ? 'block' : 'none',
                '&:focus': {
                  backgroundColor: '#e8e8e8',
                },
              }}
              disabled={
                isProcessing ||
                (mode === 'add'
                  ? createFundingNeed?.isPending
                  : updateFundingNeed?.isPending)
              }
            >
              Cancel
            </Button>
            {!(
              fundingNeedAssessment && fundingNeedAssessment.Quality !== 'Good'
            ) && (
              <Button
                type="submit"
                variant="contained"
                disableRipple
                sx={{
                  bgcolor: !isOnboarding ? '#1C3045' : '#1976d2', // Using primary blue to match the profile page
                  color: 'white',
                  px: 3,
                  py: 1,
                  fontSize: '16px',
                  fontWeight: 500,
                  textTransform: 'none',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: '#1565c0',
                  },
                }}
                disabled={
                  hasPendingUpload ||
                  isProcessing ||
                  (mode === 'add'
                    ? createFundingNeed?.isPending
                    : updateFundingNeed?.isPending)
                }
                aria-describedby={
                  hasPendingUpload
                    ? 'funding-need-save-pending-caption'
                    : undefined
                }
              >
                {isProcessing ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress
                      size={20}
                      color="inherit"
                      sx={{ mr: 1 }}
                    />
                    Processing...
                  </Box>
                ) : (
                  submitButtonText
                )}
              </Button>
            )}
          </Box>
          {mode === 'add' && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'left', mt: 1 }}
            >
              You can edit these details later if needed
            </Typography>
          )}
          {hasPendingUpload && (
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
                maxWidth: 420,
                ml: 'auto',
                gap: 1,
                mt: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                // Same derivation MUI uses for a standard warning Alert.
                // warning.dark on white was only 3.8:1 and failed WCAG AA
                // at caption size; this pairing is ~8.7:1 (#1465).
                bgcolor: lighten(theme.palette.warning.light, 0.9),
                color: darken(theme.palette.warning.light, 0.6),
                border: `1px solid ${lighten(
                  theme.palette.warning.light,
                  0.7,
                )}`,
              })}
            >
              <WarningAmberOutlinedIcon
                fontSize="small"
                sx={{ flexShrink: 0 }}
              />
              <Typography
                id="funding-need-save-pending-caption"
                role="status"
                variant="caption"
                sx={{
                  color: 'inherit',
                  textAlign: 'left',
                }}
              >
                Finish uploading or remove what's listed above before saving.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Cancel Confirmation Dialog */}
        {showCancelConfirmation && (
          <Dialog
            open={showCancelConfirmation}
            onClose={() => setShowCancelConfirmation(false)}
            aria-labelledby="cancel-dialog-title"
            aria-describedby="cancel-dialog-description"
          >
            <DialogTitle id="cancel-dialog-title">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" sx={{ mt: 1, textAlign: 'center' }}>
                  Are you sure you want to cancel?
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText
                id="cancel-dialog-description"
                sx={{
                  color: '#1C3045',
                  mb: 3,
                }}
              >
                You have unsaved changes that will be lost if you cancel. <br />{' '}
                Are you sure you want to discard your changes?
              </DialogContentText>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => setShowCancelConfirmation(false)}
                variant="outlined"
                disableRipple
                sx={{
                  color: '#1C3045',
                  borderColor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  width: '150px',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Keep Editing
              </Button>
              <Button
                onClick={() => {
                  setShowCancelConfirmation(false)
                  onCancel?.()

                  if (prefillDraft !== null && setPrefillDraft !== undefined) {
                    setPrefillDraft(null)
                  }
                }}
                variant="contained"
                disableRipple
                sx={{
                  color: 'white',
                  backgroundColor: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  width: '170px',
                }}
              >
                Discard Changes
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    )
  },
)

export default FundingNeedsForm
